(function() {
    'use strict';
    let owner = null;
    let generation = 0;
    let context = null;

    window.clearProviderInventory = function() {
        generation += 1;
        owner = null;
        context = null;
        document.getElementById('providerInventory')?.replaceChildren();
    };

    window.renderProviderInventory = function(next) {
        const host = document.getElementById('providerInventory');
        if (!host || owner === next.providerId) return;
        window.clearProviderInventory();
        owner = next.providerId;
        context = next;
        host.innerHTML = `<details><summary>Food waste and ingredient inventory</summary>
            <p>Track raw ingredients privately: opening count, purchases, recipe usage, waste and closing counts.
            Quantities are measured in g, ml or individual units; convert kg to g and litres to ml before entering.</p>
            <button type="button" data-load-inventory>Load inventory</button><div data-inventory-body></div></details>`;
        host.querySelector('[data-load-inventory]').onclick = () => load(host);
    };

    async function load(host) {
        const token = ++generation;
        const current = context;
        const body = host.querySelector('[data-inventory-body]');
        const button = host.querySelector('[data-load-inventory]');
        button.disabled = true;
        body.innerHTML = '<p role="status">Loading complete ingredient ledger...</p>';
        try {
            const result = await window.callProtectedFunction('getProviderInventory', {});
            if (!result?.ok || !result.inventory) throw new Error('The inventory service returned no data.');
            if (token !== generation) return;
            draw(body, result.inventory, current, () => load(host));
            button.textContent = 'Refresh inventory (discards unsaved edits)';
        } catch (error) {
            if (token !== generation) return;
            console.error('[Inventory] Could not load ingredient ledger:', error);
            body.innerHTML = `<p class="insights-warning" role="alert">Inventory unavailable: ${current.escape(error.message)}.
                No partial variance is shown. Use Load/Refresh inventory to retry.</p>`;
        } finally {
            if (token === generation) button.disabled = false;
        }
    }

    function draw(host, data, current, reload) {
        const escape = current.escape;
        const number = (value) => value === null ? 'Count not recorded' :
            new Intl.NumberFormat(current.locale, { maximumFractionDigits: 3 }).format(value);
        const options = data.rows.map((row) => `<option value="${escape(row.id)}">${escape(row.name)} (${escape(row.unit)})</option>`).join('');
        const coverage = data.coverage;
        host.innerHTML = `
            <div class="insights-warning" role="note">
                <strong>Inventory coverage and interpretation</strong>
                <p>Completed standard online orders deduct the recipe recorded at completion, whether paid or unpaid.
                Cancelled orders do not deduct automatically; record waste/usage if they were prepared.
                Meals with removed included components need manual actual usage rather than an inaccurate base deduction.
                Preparing/ready meals have not deducted yet; count after recording completed kitchen usage or account for this timing gap.
                Drinks, separate gift orders and unrecorded POS use need manual ingredient entries.
                Missing recipe/options produce no automatic deduction, not a zero-use estimate.</p>
                <p>${coverage.untrackedOrders} completed orders since inventory setup have no usage event.
                ${coverage.incompleteOrders} order events have missing recipe quantities.
                ${coverage.posDays} POS days are on file; manually record their ingredient usage with a POS reference.
                POS completeness is not automatically verified.</p>
                <p>Unexplained variance = actual count - expected stock. Negative means shortage; positive means surplus.
                This can reflect spoilage, over-portioning, unlogged usage or counting errors; it is not evidence of theft.
                Two counts are needed for a measured variance. Each new count starts the next reconciliation cycle.
                Purchases alone are not waste. Expected stock is a recipe estimate, not a stockout guarantee.</p>
            </div>
            <div class="insights-scroll"><table class="insights-table" data-inventory-table>
                <caption>Ingredient stock since the latest physical count</caption>
                <thead><tr><th>Ingredient</th><th>Unit</th><th>Purchases</th><th>Recipe/manual use</th><th>Logged waste</th>
                <th>Expected on hand</th><th>Last count</th><th>Latest counted variance</th></tr></thead>
                <tbody>${data.rows.map((row) => `<tr><th>${escape(row.name)}</th><td>${escape(row.unit)}</td>
                <td>${number(row.purchases)}</td><td>${number(row.usage)}</td><td>${number(row.waste)}</td>
                <td>${number(row.expectedOnHand)}${row.expectedOnHand < 0 ? ' (negative stock: check missing purchases/usage)' : ''}</td>
                <td>${escape(row.lastCount || 'Not recorded')}</td>
                <td>${row.comparison ? `${number(row.comparison.variance)} ${escape(row.unit)}
                    (expected ${number(row.comparison.expected)}, actual ${number(row.comparison.actual)})
                    at ${escape(row.comparison.countedAt)}; purchases ${number(row.comparison.purchases)},
                    use ${number(row.comparison.usage)}, waste ${number(row.comparison.waste)}` : 'Two counts required'}</td></tr>`).join('') ||
                    '<tr><td colspan="8">Add ingredients and record opening counts to begin.</td></tr>'}</tbody></table></div>
            <details><summary>Add ingredient</summary>
                <form data-ingredient-form>
                    <label>Name <input name="name" required maxlength="100"></label>
                    <label>Fixed unit <select name="unit"><option value="g">Grams (g)</option>
                        <option value="ml">Millilitres (ml)</option><option value="unit">Individual units</option></select></label>
                    <p>Names and units cannot be changed after creation, to protect historical measurements.</p>
                    <button type="submit">Add private ingredient</button><p data-save-status role="status"></p>
                </form>
            </details>
            <details><summary>Package prices and usable yield</summary>
                <p>Latest saved package price is a replacement-cost estimate, not weighted-average inventory valuation.
                    Package contents use the ingredient's fixed unit. Saving a price does not record a stock purchase.
                    Yield accounts for expected trimming or bottle residue; continue recording actual stock waste separately.
                    Recipe quantities describe usable food, while counts/purchases describe raw stock; non-100% yield losses
                    must be logged as waste for inventory variance to reconcile.</p>
                <label>Ingredient <select data-package-menu><option value="">Choose ingredient</option>${options}</select></label>
                <form data-package-form hidden></form>
            </details>
            <details><summary>Recipe costing, package servings and suggested price</summary>
                <p>Save package prices and recipe quantities first. Select a portion/add-ons to calculate ingredient cost
                    and full servings from each usable package. Yield is an estimate; package servings are not total meals available across your stock.</p>
                <label>Meal <select data-costing-menu><option value="">Choose meal</option>${data.menu.map((item) =>
                    `<option value="${escape(item.id)}">${escape(item.name)}</option>`).join('')}</select></label>
                <form data-costing-form hidden></form>
            </details>
            <details><summary>Record purchase, waste, POS usage or stock count</summary>
                <form data-event-form>
                    <label>Ingredient <select name="ingredientId" required><option value="">Choose ingredient</option>${options}</select></label>
                    <label>Record type <select name="kind">
                        <option value="purchase">Purchase received</option><option value="waste">Waste / spoilage</option>
                        <option value="usage">Ingredient usage</option><option value="count">Physical stock count</option></select></label>
                    <label>Quantity in ingredient's fixed unit <input name="quantity" type="number" min="0" step="0.001" max="100000000" required></label>
                    <label>Event time (your browser timezone) <input name="occurredAt" type="datetime-local" step="1" required></label>
                    <p>Leaving the default time unchanged records the time of submission. Enter a different time for a backdated record.</p>
                    <label>Usage source <select name="source"><option value="pos">Walk-in POS</option><option value="manual">Other / missing online recipe / gifts / drinks</option></select></label>
                    <label>Receipt reference, waste reason or count/usage note <input name="reason" required maxlength="300"></label>
                    <p>For POS, calculate ingredients from your recipe and meals sold, then record each ingredient once
                    with the POS date/receipt reference. Do not record online usage again if automatically deducted.
                    Count zero is valid; purchases, waste and usage must be positive. Entries are an append-only audit log;
                    correct stock with a new physical count, not by deleting history. Backdated entries recalculate affected comparisons.</p>
                    <button type="submit">Record inventory event</button><p data-save-status role="status"></p>
                </form>
            </details>
            <details><summary>Recipe quantities per serving</summary>
                <p>Base ingredients plus additional portion/add-on quantities. Costs remain separate in Private menu costs.
                    Save explicit empty option recipes when there is no added ingredient use; leave unknown options unrecorded.
                    Regular uses base ingredients unless an adjustment is saved. Recipes apply to future completions only.</p>
                <label>Meal <select data-recipe-menu><option value="">Choose meal</option>${data.menu.map((item) =>
                    `<option value="${escape(item.id)}">${escape(item.name)}</option>`).join('')}</select></label>
                <form data-recipe-form hidden></form>
            </details>
            <details><summary>Recent inventory audit entries (latest 100; calculations use the complete ledger)</summary>
                <ul>${data.events.slice(0, 100).map((event) => `<li>${escape(event.occurredAt)}: ${escape(event.kind)}
                    (${escape(event.source)}) ${escape(event.reason || event.orderId || '')}
                    ${(event.lines || []).map((line) => `${escape(data.rows.find((row) => row.id === line.ingredientId)?.name || line.ingredientId)}:
                    ${number(line.quantity)} ${escape(line.unit)}`).join('; ')}
                    ${(event.missing || []).length ? ` - NOT DEDUCTED: ${escape(event.missing.join('; '))}` : ''}</li>`).join('')}</ul>
            </details>`;
        const ingredientForm = host.querySelector('[data-ingredient-form]');
        bindSave(ingredientForm, () => ({ action: 'ingredient', ...Object.fromEntries(new FormData(ingredientForm)) }), current, reload);
        const eventForm = host.querySelector('[data-event-form]');
        const local = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 19);
        eventForm.elements.occurredAt.value = local;
        bindSave(eventForm, () => {
            const values = Object.fromEntries(new FormData(eventForm));
            return { ...values, action: 'event', quantity: Number(values.quantity),
                occurredAt: values.occurredAt === local ? new Date().toISOString() : new Date(values.occurredAt).toISOString() };
        }, current, reload);
        host.querySelector('[data-recipe-menu]').onchange = (event) =>
            recipeForm(host.querySelector('[data-recipe-form]'), data, event.target.value, current, reload);
        host.querySelector('[data-package-menu]').onchange = (event) =>
            packageForm(host.querySelector('[data-package-form]'), data.rows.find((row) => row.id === event.target.value), current, reload, data.currency || 'XCD');
        host.querySelector('[data-costing-menu]').onchange = (event) =>
            costingForm(host.querySelector('[data-costing-form]'), data, event.target.value, current, reload);
    }

    function bindSave(form, payload, current, reload, fixedId = null) {
        let request = null;
        form.onsubmit = async (event) => {
            event.preventDefault();
            const status = form.querySelector('[data-save-status]');
            const button = form.querySelector('button[type="submit"]');
            button.disabled = true;
            try {
                // Preserve the exact submission after network errors to prevent double recording.
                if (!request) request = { ...payload(), id: fixedId || window.crypto.randomUUID() };
                status.textContent = 'Saving...';
                const result = await window.callProtectedFunction('saveProviderInventory', request);
                if (!result?.ok) throw new Error('The inventory service did not confirm the save.');
                if (owner !== current.providerId || context !== current) return;
                if (request.action === 'applyCosts') {
                    window.showNotification('Calculated recipe costs applied to future orders. Menu prices and historical costs are unchanged.', 'success');
                } else {
                window.showNotification('Private inventory saved.', 'success');
                }
                await reload();
            } catch (error) {
                console.error('[Inventory] Could not save inventory:', error);
                if (owner !== current.providerId || context !== current) return;
                status.textContent = `Not saved: ${error.message}. Retry submits the same entry. Reload inventory to discard it and edit.`;
                button.disabled = false;
            }
        };
    }

    function packageForm(form, ingredient, current, reload, currency) {
        form.hidden = !ingredient;
        if (!ingredient) return;
        const escape = current.escape;
        const pack = ingredient.package || {};
        form.innerHTML = `<label>Contents per package (${escape(ingredient.unit)})
            <input name="size" type="number" min="0.001" max="100000000" step="0.001" required value="${pack.size ?? ''}"></label>
            <label>Price per package (exclude recoverable purchase tax)
            <input name="price" type="number" min="0" max="100000" step="0.01" required value="${pack.price ?? ''}"></label>
            <label>Currency <input name="currency" pattern="[A-Z]{3}" maxlength="3" required value="${escape(pack.currency || currency)}"></label>
            <label>Usable yield (%) <input name="yieldPercent" type="number" min="0.001" max="100" step="0.001" required value="${pack.yieldPercent ?? 100}"></label>
            <p data-package-preview role="status"></p>
            <button type="submit">Save package price</button><p data-save-status role="status"></p>`;
        const preview = () => {
            const values = Object.fromEntries(new FormData(form));
            const result = window.WTEIngredientCosting.packageCost({
                size: values.size === '' ? null : Number(values.size),
                price: values.price === '' ? null : Number(values.price),
                yieldPercent: values.yieldPercent === '' ? null : Number(values.yieldPercent), currency: values.currency
            }, values.currency);
            form.querySelector('[data-package-preview]').textContent = result && form.checkValidity()
                ? `Usable contents: ${result.usable.toFixed(3)} ${ingredient.unit}. Cost per ${ingredient.unit}: ${result.unitCost.toFixed(6)} ${values.currency}.`
                : 'Enter valid package contents, price, currency and usable yield.';
        };
        form.oninput = preview;
        preview();
        bindSave(form, () => {
            const values = Object.fromEntries(new FormData(form));
            return { action: 'package', size: Number(values.size), price: Number(values.price),
                yieldPercent: Number(values.yieldPercent), currency: values.currency };
        }, current, reload, ingredient.id);
    }

    function costingForm(form, data, menuId, current, reload) {
        const item = data.menu.find((row) => row.id === menuId);
        form.hidden = !item;
        if (!item) return;
        const escape = current.escape;
        const recipe = data.recipes.find((row) => row.id === menuId);
        const currency = item.currency || 'XCD';
        const money = (value) => new Intl.NumberFormat(current.locale, { style: 'currency', currency }).format(value);
        form.innerHTML = `<label>Portion <select data-costing-portion>${(item.portions.length ? item.portions : ['Regular'])
            .map((portion) => `<option value="${escape(portion)}">${escape(portion)}</option>`).join('')}</select></label>
            ${item.extras.map((extra) => `<label><input type="checkbox" data-costing-extra="${escape(extra.key)}">
                Include ${escape(extra.name)}</label>`).join('')}
            <label>Packaging cost per serving (${escape(currency)})
                <input name="packagingCost" type="number" min="0" max="100000" step="0.01" required value="${item.costs?.packagingCost ?? ''}"></label>
            <label>Test base selling price (${escape(currency)}, excluding tax/drinks)
                <input data-costing-price type="number" min="0" step="0.01" required value="${item.price ?? ''}"></label>
            <label>Target food gross margin (%)
                <input data-costing-target type="number" min="0" max="99.999" step="0.001" required value="60"></label>
            <div data-costing-preview role="status" aria-live="polite"></div>
            <p>Current ${item.specialActive ? 'active Daily Special' : 'menu'} base price plus portion/add-on price adjustments is used by default.
                Suggestions cover food and packaging, not labour, rent, delivery or payment fees. Target margin is not markup.
                Suggested price is the whole selected meal price, not its base menu price; remove option charges before manually updating a base price.</p>
            <p>Apply replaces this meal's base and recorded option ingredient costs, plus packaging, in Private menu costs.
                Unrecorded options remain unknown. Manual additional packaging/other adjustments must be reviewed there afterwards.
                Applying costs does not change menu prices, ingredient usage, or historical sale snapshots.
                Test inputs are not saved as menu prices or pricing rules. Package changes require reviewing and applying costs again.</p>
            <button type="submit">Apply calculated costs to future orders</button><p data-save-status role="status"></p>`;
        let result = null;
        const preview = () => {
            const amount = (input) => input.value !== '' && input.validity.valid ? Number(input.value) : null;
            const portion = form.querySelector('[data-costing-portion]').value;
            const extras = Array.from(form.querySelectorAll('[data-costing-extra]:checked')).map((input) => input.dataset.costingExtra);
            let price = amount(form.querySelector('[data-costing-price]'));
            const adjustment = item.portions.length
                ? item.portionPrices?.find((row) => row.key === portion)?.price : 0;
            if (price !== null && Number.isFinite(adjustment)) price += adjustment;
            else price = null;
            for (const id of extras) {
                const extraPrice = item.extras.find((row) => row.key === id)?.price;
                if (price !== null && Number.isFinite(extraPrice)) price += extraPrice;
                else price = null;
            }
            result = window.WTEIngredientCosting.estimate(recipe, data.rows, currency, portion, extras,
                amount(form.elements.packagingCost), price, amount(form.querySelector('[data-costing-target]')));
            const allCostsKnown = recipe && [recipe.base, ...recipe.portions.map((row) => row.lines),
                ...recipe.extras.map((row) => row.lines)].every((rows) =>
                window.WTEIngredientCosting.costLines(rows, data.rows, currency).total !== null);
            form.querySelector('button[type="submit"]').disabled = !!result.error || !allCostsKnown || !item.costRevision;
            form.querySelector('[data-costing-preview]').innerHTML =
                `${result.rows ? `<div class="insights-scroll"><table class="insights-table"><thead><tr><th>Ingredient</th>
                    <th>Use per serving</th><th>Cost per unit</th><th>Ingredient cost</th><th>Full servings per usable package</th></tr></thead>
                    <tbody>${result.rows.map((row) => `<tr><th>${escape(row.name)}</th><td>${row.quantity} ${escape(row.unit)}</td>
                    <td>${row.unitCost === null ? 'Not recorded' : `${row.unitCost.toFixed(6)} ${escape(currency)}`}</td>
                    <td>${row.cost === null ? 'Not recorded' : money(row.cost)}</td>
                    <td>${row.servingsPerPackage === null ? 'Not recorded' : row.servingsPerPackage}</td></tr>`).join('')}</tbody></table></div>` : ''}
                ${result.error ? `<p class="insights-warning">${escape(result.error)}</p>` :
                    `<p>Ingredient cost: ${money(result.ingredientCost)} · Total cost: ${money(result.totalCost)} ·
                    Meal revenue: ${money(price)} · Food gross profit: ${money(result.profit)} ·
                    Margin: ${result.margin === null ? 'Unavailable at zero price' : `${result.margin.toFixed(2)}%`} ·
                    Suggested meal price: <strong>${money(result.suggestedPrice)}</strong></p>
                    ${result.profit < 0 ? '<p class="insights-warning">Current price is below recorded food and packaging cost.</p>' : ''}`}
                ${!allCostsKnown && !result.error ? '<p class="insights-warning">Some other recorded recipe options lack package prices. Complete those before applying costs.</p>' : ''}`;
        };
        form.oninput = preview;
        form.onchange = preview;
        preview();
        bindSave(form, () => {
            preview();
            if (result.error) throw new Error(result.error);
            return { action: 'applyCosts', packagingCost: Number(form.elements.packagingCost.value), costRevision: item.costRevision };
        }, current, reload, item.id);
    }

    function recipeForm(form, data, menuId, current, reload) {
        const item = data.menu.find((row) => row.id === menuId);
        form.hidden = !item;
        if (!item) return;
        const escape = current.escape;
        const saved = data.recipes.find((row) => row.id === menuId) || { base: [], portions: [], extras: [] };
        const scopes = [{ label: 'Base ingredients', type: 'base', key: '', known: true, lines: saved.base },
            ...item.portions.map((key) => {
                const row = saved.portions.find((option) => option.key === key);
                return { label: `Additional portion: ${key}`, type: 'portion', key, known: !!row, lines: row?.lines || [] };
            }), ...item.extras.map((extra) => {
                const row = saved.extras.find((option) => option.key === extra.key);
                return { label: `Additional add-on: ${extra.name}`, type: 'extra', key: extra.key, known: !!row, lines: row?.lines || [] };
            })];
        form.innerHTML = `<label>Recipe section <select data-recipe-scope>${scopes.map((row, index) =>
            `<option value="${index}">${escape(row.label)}</option>`).join('')}</select></label>
            <div data-recipe-lines></div><button type="submit">Save ingredient recipe</button><p data-save-status role="status"></p>`;
        let index = 0;
        const capture = () => {
            scopes[index].known = index === 0 || form.querySelector('[data-recipe-known]').checked;
            scopes[index].lines = Array.from(form.querySelectorAll('[data-ingredient-quantity]'))
                .filter((input) => input.value !== '').map((input) =>
                    ({ ingredientId: input.dataset.ingredientQuantity, quantity: Number(input.value) }));
        };
        const paint = () => {
            const scope = scopes[index];
            form.querySelector('[data-recipe-lines]').innerHTML =
                `${index ? `<label><input type="checkbox" data-recipe-known ${scope.known ? 'checked' : ''}> Additional quantities are recorded (empty means no added use)</label>` : ''}
                ${data.rows.map((ingredient) => `<label>${escape(ingredient.name)} (${escape(ingredient.unit)} / serving)
                    <input type="number" min="0.001" step="0.001" max="100000000" data-ingredient-quantity="${escape(ingredient.id)}"
                    value="${scope.lines.find((line) => line.ingredientId === ingredient.id)?.quantity ?? ''}"></label>`).join('')}`;
        };
        form.querySelector('[data-recipe-scope]').onchange = (event) => {
            capture();
            index = Number(event.target.value);
            paint();
        };
        paint();
        bindSave(form, () => {
            capture();
            return { action: 'recipe', base: scopes[0].lines,
                portions: scopes.filter((row) => row.type === 'portion' && row.known).map(({ key, lines }) => ({ key, lines })),
                extras: scopes.filter((row) => row.type === 'extra' && row.known).map(({ key, lines }) => ({ key, lines })) };
        }, current, reload, menuId);
    }
})();
